import React from "react";
import Tooltip from "../../../../../components/Tooltip/Tooltip";
import type { DepGraph, DepGraphActivity } from "../../../../../models";
import type {
  DepCandidate,
  ExternalCandidate,
} from "../../DepChipTypeahead/DepChipTypeahead";
import type { SeveredRowInfo } from "../../anchorLayout";
import {
  driverDescriptor,
  kindFromTaskType,
  nodeStatus,
  predsFor,
  rowFinish,
  rowStart,
  type ResolvedSucc,
} from "../../graphRows";
import ChipCell from "../ChipCell/ChipCell";
import type { DepEditIntents } from "../intents";

const SELECT_IGNORE_SELECTOR = "button, input, .dep-chip-popover, .dep-typeahead";

interface Props {
  activity: DepGraphActivity;
  anchorKeys: string[];
  isSelected: boolean;
  graph: DepGraph;
  succs: ResolvedSucc[];
  canSelect: boolean;
  canDelete: boolean;
  tableReadOnly: boolean;
  allowExternalEdits: boolean;
  cycleEdges?: Set<string>;
  zonesByCode: Map<string, Set<string>>;
  allCandidates: DepCandidate[];
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  intents: DepEditIntents;
  reviewedAnchors?: Set<string>;
  onToggleAnchorReview?: (key: string) => void;
  severedInfo?: Map<string, SeveredRowInfo>;
  anchorDirections?: Map<string, "in" | "out">;
  fmtDate: (iso: string | null) => string;
  onSelect: (id: string) => void;
  attachRowRef: (el: HTMLTableRowElement | null) => void;
}

const AnchorRow: React.FC<Props> = ({
  activity: act,
  anchorKeys,
  isSelected,
  graph,
  succs,
  canSelect,
  canDelete,
  tableReadOnly,
  allowExternalEdits,
  cycleEdges,
  zonesByCode,
  allCandidates,
  fetchExternals,
  intents,
  reviewedAnchors,
  onToggleAnchorReview,
  severedInfo,
  anchorDirections,
  fmtDate,
  onSelect,
  attachRowRef,
}) => {
  const preds = predsFor(graph, act.id);
  const kind = kindFromTaskType(act.task_type);
  const isNonTask = kind !== "task";
  const status = nodeStatus(act);

  const { done, origin } = driverDescriptor(act);
  const reviewed =
    anchorKeys.length > 0 &&
    anchorKeys.every((k) => reviewedAnchors?.has(k) ?? false);
  const severed = severedInfo?.get(act.id);
  const direction = anchorDirections?.get(act.id);
  const chipsReadOnly = tableReadOnly || !allowExternalEdits;
  const editableSide: "pred" | "succ" | null =
    direction == null ? null : direction === "in" ? "succ" : "pred";
  const anchorSide: "pred" | "succ" = direction === "out" ? "pred" : "succ";
  const addLabel =
    direction === "in" ? "+ Incoming successor" : "+ Outgoing pred";
  const addTitle =
    direction === "in"
      ? "Add a section activity this outside work feeds (it becomes the external's successor)"
      : "Add a section activity this outside work waits on (it becomes the external's predecessor)";
  return (
    <tr
      ref={attachRowRef}
      className={
        "dep-table-row--anchor" +
        (isSelected ? " dep-table-row--selected" : "") +
        (onToggleAnchorReview && reviewed
          ? " dep-table-row--anchor-reviewed"
          : "")
      }
      onClick={(e) => {
        if ((e.target as HTMLElement).closest(SELECT_IGNORE_SELECTOR)) return;
        onSelect(act.id);
      }}
    >
      {canSelect && (
        <td className="dep-table-col-select">
          {onToggleAnchorReview && (
            <input
              type="checkbox"
              className="dep-table-select-cb dep-table-anchor-cb"
              checked={reviewed}
              title={
                reviewed
                  ? "Reviewed — this outside activity's connections work for the new plan"
                  : "Check off once this outside activity's connections make sense for the new plan"
              }
              onChange={() => {
                const flips = reviewed
                  ? anchorKeys
                  : anchorKeys.filter((k) => !(reviewedAnchors?.has(k) ?? false));
                for (const k of flips) onToggleAnchorReview(k);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </td>
      )}
      <td className="dep-table-col-name">
        <div className="dep-table-name-row">
          {direction && (
            <span
              className="dep-table-anchor-arrow"
              aria-hidden="true"
              title={
                direction === "in"
                  ? "Incoming — feeds work in this section"
                  : "Outgoing — waits on work in this section"
              }
            >
              {direction === "in" ? "←" : "→"}
            </span>
          )}
          <Tooltip content={`${act.task_code} · ${act.name}`}>
            <span className="dep-table-anchor-name">
              <span className="dep-table-anchor-name-text">{act.name}</span>
              {act.task_code && (
                <span className="dep-table-chip-taskcode">{act.task_code}</span>
              )}
            </span>
          </Tooltip>
          <span
            className="dep-table-origin-tag dep-table-origin-tag--outside"
            title={
              done
                ? "Finished work tied to this section"
                : `Lives outside this section — in ${origin}`
            }
          >
            {done ? "✓" : "↗"} outside · {origin}
          </span>
          {severed?.disconnected && (
            <span
              className="dep-table-anchor-severed-flag"
              title="Every tie this anchor had into the section was severed by a staged delete — add a tie to a section activity (or undo the delete) to resolve it"
            >
              ⚠ tie severed
            </span>
          )}
          {status === "active" && (
            <span
              className="dep-table-status dep-table-status--active"
              title="In progress — started, not yet finished"
            >
              ●
            </span>
          )}
        </div>
        {severed && severed.partners.length > 0 && (
          <div className="dep-table-anchor-was-tied">
            was tied to
            {severed.partners.map((n) => (
              <span key={n} className="dep-table-ghost-chip">
                {n}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">{fmtDate(rowStart(act))}</span>
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">{fmtDate(rowFinish(act))}</span>
      </td>
      <td className="dep-table-col-dur">
        {isNonTask ? "—" : `${act.duration_days}d`}
      </td>
      <td className="dep-table-col-chips">
        <ChipCell
          cycleEdges={cycleEdges}
          row={act}
          side={anchorSide}
          preds={preds}
          succs={succs}
          readOnly={chipsReadOnly || editableSide !== anchorSide}
          allowExternalEdits={allowExternalEdits && !tableReadOnly}
          addLabel={addLabel}
          addTitle={addTitle}
          ghosts={editableSide === anchorSide ? severed?.partners : undefined}
          zonesByCode={zonesByCode}
          allCandidates={allCandidates}
          fetchExternals={fetchExternals}
          intents={intents}
        />
      </td>
      {canDelete && <td className="dep-table-col-actions" />}
    </tr>
  );
};

export default AnchorRow;
